import { mdiClose, mdiFilterVariant } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { Overlay } from '../../../base';
import { FormFieldGroup } from '../../../form-field';
import { DataTablePresenter } from '../../data-table.presenter';
import { FilterItem } from './filter-item';
import { CustomFilterFieldName, ICustomFilter } from './filters.models';
import { methodForField } from './filters.service';
import * as S from './filters.styles';

interface IFiltersProps {
    presenter: DataTablePresenter<any>;
}

export const Filters: React.FunctionComponent<IFiltersProps> = observer(({ presenter }) => {
    const {
        smartFilterValue,
        enableSmartFilter,
        filters,
        filterSchemas,
        onFilterAdd,
        onFilterRemove,
        onFiltersChange,
        onSmartFilterChange,
    } = presenter;

    const renderButton = (onOpen: (target: EventTarget) => void) => {
        const handleClick = (e: React.MouseEvent): void => onOpen(e.currentTarget);
        const handleChange = (e: React.FormEvent<HTMLInputElement>) => onSmartFilterChange(e.currentTarget.value);
        return (
            <S.ActionsWrapper>
                <S.ActionButton variant="outlined" onClick={handleClick}>
                    <Icon path={mdiFilterVariant} size={0.75} />
                    <span>Filter</span>
                </S.ActionButton>
                {!!enableSmartFilter && (
                    <S.FilterInput placeholder="Search..." value={smartFilterValue} onChange={handleChange} />
                )}
            </S.ActionsWrapper>
        );
    };

    const handleAddFilter = () => {
        onFilterAdd({
            filterType: 'String',
            value: '',
        });
    };

    const handleRemoveFilter = (filter: ICustomFilter) => {
        return () => onFilterRemove(filter);
    };

    const handleChange = (filter: ICustomFilter, name: CustomFilterFieldName, value: any) => {
        const changedFilter: ICustomFilter = {
            ...filter,
            [name]: value,
        };

        // Remove value if we changed Filter ID
        if (name === 'filterId') {
            changedFilter.value = undefined;
        }

        // Resolve the schema from the filterId being SET, not the one being replaced. Looking it up
        // on `filter.filterId` left `filterType` one interaction behind the column: pick a field and
        // the filter still carried the previous field's type until something else was touched.
        // It was survivable while every text-ish field parsed identically; it is not now, because
        // `parseFilter` switches on `filterType` and a MultiString mis-typed as String sends the
        // whole pasted block as ONE value — which matches nothing and reports it as "no results".
        const nextFilterId = name === 'filterId' ? value : filter.filterId;
        const filterSchema = filterSchemas.find(schema => schema.filterId === nextFilterId);
        if (filterSchema) {
            changedFilter.filterType = filterSchema.filterType;

            // A new field may not offer the old method. Carrying it over hid it — the method select
            // showed blank — while it was still sent: "Payer · Not equal" switched to ID went out
            // as `NQ:a,NQ:b` and returned every claim. Keep it only where the new field offers it.
            if (name === 'filterId') {
                changedFilter.filterMethod = methodForField(filter.filterMethod, filterSchema.filterType);
            }
        }

        onFiltersChange(filter, changedFilter);
    };

    return (
        <S.FiltersContainer>
            <Overlay
                renderButton={renderButton}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
                <S.FilterWrapper>
                    <FormFieldGroup onAdd={handleAddFilter} onAddLabel="Add a Filter">
                        {filters.map((filter, i) => {
                            const onChange = (name: CustomFilterFieldName, value: any) => {
                                handleChange(filter, name, value);
                            };
                            return (
                                <S.FilterGroup key={`${i}-${filter.filterId}`}>
                                    <FilterItem
                                        filterId={filter.filterId}
                                        filterMethod={filter.filterMethod}
                                        filterValue={filter.value}
                                        index={i}
                                        filterSchemas={filterSchemas}
                                        onChange={onChange}
                                    />

                                    {!filter.locked && (
                                        <S.DeleteButton
                                            variant="text"
                                            type="button"
                                            onClick={handleRemoveFilter(filter)}>
                                            <Icon path={mdiClose} size={0.75} />
                                        </S.DeleteButton>
                                    )}
                                </S.FilterGroup>
                            );
                        })}
                    </FormFieldGroup>
                </S.FilterWrapper>
            </Overlay>
        </S.FiltersContainer>
    );
});
